import { FC, JSX } from "react";
import LoadingSpinner from "../LoadingSpinner";

interface DashboardTileProps {
  /**
   * Optional background color string, if desired bg color is different from inherited page color
   */
  backgroundColor?: string;
  /**
   * The element that is rendered in the tile's body
   */
  children: React.ReactNode;
  /**
   * Optional classnames applied to the outermost tile container div
   */
  classNames?: string;
  /**
   * An optional element rendered to the far right of the header. For example, a dropdown used to modify the contents of the tile.
   */
  headerElement?: JSX.Element;
  /**
   * A boolean used to show the loading spinner, intended for use when the content isn't ready yet.<br/>
   * Docs note: This won't work in Storybook because the LoadingSpinner component relies entirely on Bootstrap.
   */
  loading?: boolean;
  //contextMenuItems?: JSX.Element[];
  //onContextMenuClick?: () => void;
  /**
   * Optional boolean to show a dividing line between the header and body of the tile
   */
  showTitleSeparator?: boolean;
  /**
   * Optional string to show just below the title of the tile
   */
  subtitle?: string | JSX.Element;
  /**
   * The title of the tile, rendered boldly in the top left
   */
  title: string | JSX.Element;
  /**
   * A required string, intended to be one of Bootstrap's col-* classes.
   */
  widthClass: string;
}

/**
 * A tile component used on newer Dashboards in QES, like the Enterprice Compliance Dashboard and Provider Claims Dashboards. <br /> <br />
 * Docs note: The Rotating variant is actually a separate component based on this tile. It doesn't currently support several of the props listed here for DashboardTile, and instead of children it has front and back props.<br /><br />
 * Docs note: The loading prop currently does not work in Storybook because the spinner relies on Bootstrap/Looper styles.<br /><br />
 */
const DashboardTile: FC<DashboardTileProps> = (props: DashboardTileProps) => {
  return (
    <div className={`qes-dashboard-tile-container col-12 ${props.widthClass} ${props.classNames}`}>
      <div className="qes-dashboard-tile" style={{backgroundColor: props.backgroundColor}}>
        <div className="qes-dashboard-tile__header">
          <h2 className="qes-dashboard-tile__title">{props.title}</h2>
          {!!props.subtitle && (
            <span className="qes-dashboard-tile__subtitle">{props.subtitle}</span>
            )}
            {!!props.headerElement && (
              <span className="qes-dashboard-tile__header-element">
                {props.headerElement}
              </span>
            )}
          {!!props.showTitleSeparator && <hr className="qes-dashboard-tile__separator" />}
        </div>
        <div className="qes-dashboard-tile__body">
          {props.loading ? <LoadingSpinner /> : props.children}
        </div>
      </div>
    </div>
  )
}

export default DashboardTile;
